import { MOD_ENTER, TaskActionBar } from '@/features/contribution-kit';
import type { ValidateTranscriptionCopy } from './validate-transcription-copy';

type ValidateTranscriptionActionBarProps = {
  t: Record<string, unknown>;
  copy: ValidateTranscriptionCopy;
  isFirst: boolean;
  text: string;
  rejectionReason: string;
  isLoading: boolean;
  isSubmitting: boolean;
  hasItem: boolean;
  onPrevious: () => void;
  onReject: () => void;
  onValidate: () => void;
  onNext: () => void;
  onTogglePlay: () => void;
};

/**
 * Bottom bar of "Valider des textes": previous, reject, validate, next. The
 * enabled rules of the former buttons are kept: validate needs a text,
 * reject needs a reason, both wait for the running request. Next on the last
 * item only says the session is over, as before.
 * Keys: Space plays, V validates, R rejects, arrows move (never while typing).
 */
export function ValidateTranscriptionActionBar({
  t,
  copy,
  isFirst,
  text,
  rejectionReason,
  isLoading,
  isSubmitting,
  hasItem,
  onPrevious,
  onReject,
  onValidate,
  onNext,
  onTogglePlay,
}: ValidateTranscriptionActionBarProps) {
  const s = (key: string) => String(t[key] ?? '');
  return (
    <TaskActionBar
      actions={[
        { key: 'previous', label: s('previous'), icon: 'arrowLeft', onClick: onPrevious, disabled: isFirst, shortcut: 'ArrowLeft' },
        {
          key: 'reject',
          label: s('reject'),
          icon: 'xCircle',
          tone: 'danger',
          onClick: onReject,
          disabled: isLoading || !rejectionReason,
          loading: isSubmitting,
          shortcut: 'r',
          testId: 'validate-transcription-reject',
        },
        {
          key: 'validate',
          label: s('validate'),
          icon: 'checkCircle',
          tone: 'primary',
          onClick: onValidate,
          disabled: isLoading || !text.trim(),
          loading: isSubmitting,
          shortcut: 'v',
          testId: 'validate-transcription-validate',
        },
        { key: 'next', label: s('next'), icon: 'arrowRight', onClick: onNext, shortcut: 'ArrowRight' },
      ]}
      extraShortcuts={[
        { key: ' ', run: onTogglePlay, enabled: hasItem },
        // V can't work while correcting the text: Ctrl/Cmd+Enter validates from the field.
        { key: MOD_ENTER, run: onValidate, enabled: !isLoading && !isSubmitting && text.trim().length > 0 },
      ]}
      hints={[
        { key: ' ', label: copy.play },
        { key: MOD_ENTER, label: s('validate') },
      ]}
    />
  );
}
